import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Check,
  Code2,
  Layers3,
  Menu,
  Moon,
  Smartphone,
  Sun,
  X
} from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { type Project, projects } from '../data/projects'
import Background from './background'

export type SiteViewProps = {
  page?: 'home' | 'projects' | 'content'
  children?: ReactNode
  links?: { home: string; projects: string; support: string; terms: string; privacy: string }
  onLocaleChange?: (locale: 'ja' | 'en') => void
  onThemeChange?: (theme: 'light' | 'dark') => void
  locale?: 'ja' | 'en'
  theme?: 'light' | 'dark'
}

type Filter = 'all' | Project['category']

function ProjectArtwork({ project }: { project: Project }) {
  return (
    <div className={`nkb-art nkb-art-${project.visual}`} aria-hidden='true'>
      <span className='nkb-art-index'>NEVER KNOWS BEST / {project.category.toUpperCase()}</span>
      <div className='nkb-art-shape'>
        {project.visual === 'music' && (
          <div className='nkb-wave'>
            {Array.from({ length: 19 }, (_, i) => 24 + ((i * 17) % 65)).map((height) => (
              <i key={height} style={{ height: `${height}%` }} />
            ))}
          </div>
        )}
        {project.visual === 'transfer' && (
          <div className='nkb-transfer'>
            <span>▧</span>
            <ArrowRight size={42} />
            <span>▧</span>
          </div>
        )}
        {project.visual === 'chart' && (
          <div className='nkb-chart'>
            {[30, 48, 38, 65, 58, 78, 72, 92].map((height) => (
              <i key={height} style={{ height: `${height}%` }} />
            ))}
          </div>
        )}
        {project.visual === 'mystery' && (
          <div className='nkb-mystery'>
            <span>“</span>
            <i />
            <span>?</span>
          </div>
        )}
        {project.visual === 'qr' && (
          <div className='nkb-qr'>
            <i />
            <i />
            <i />
            <span />
          </div>
        )}
        {project.visual === 'nutrition' && (
          <div className='nkb-ring'>
            <span>v.</span>
          </div>
        )}
        {project.visual === 'writing' && (
          <div className='nkb-writing'>
            <span>Aa</span>
            <i />
            <i />
            <i />
          </div>
        )}
        {project.visual === 'terminal' && (
          <div className='nkb-terminal'>
            <span>&gt;_</span>
          </div>
        )}
      </div>
      <strong className='nkb-art-name'>{project.name}</strong>
      <span className='nkb-art-corner'>↗</span>
    </div>
  )
}

function ProjectCard({ project, locale }: { project: Project; locale: 'ja' | 'en' }) {
  const japanese = locale === 'ja'
  return (
    <article className='nkb-project-card'>
      <ProjectArtwork project={project} />
      <div className='nkb-project-info'>
        <div className='nkb-project-heading'>
          <h3>{project.name}</h3>
          <span className={`nkb-status ${project.status === 'store' ? 'nkb-status-store' : ''}`}>
            <i />
            {project.status === 'store' ? 'App Store' : japanese ? '開発プロジェクト' : 'Development'}
          </span>
        </div>
        <p className='nkb-project-tagline'>{project.description[locale]}</p>
        <p className='nkb-project-detail'>{project.detail[locale]}</p>
        <div className='nkb-project-bottom'>
          <div className='nkb-tags'>
            {project.technologies.map((technology) => (
              <span key={technology}>{technology}</span>
            ))}
          </div>
          <a href={project.repository} target='_blank' rel='noreferrer' aria-label={`${project.name} GitHub`}>
            GitHub <ArrowUpRight size={14} />
          </a>
        </div>
        {project.store && (
          <a className='nkb-store-link' href={project.store} target='_blank' rel='noreferrer'>
            {japanese ? 'App Storeで見る' : 'View on the App Store'} <ArrowUpRight size={14} />
          </a>
        )}
      </div>
    </article>
  )
}

export function SiteView({
  page = 'home',
  locale = 'ja',
  theme = 'light',
  children,
  links = {
    home: '/',
    projects: '/product',
    support: '/support',
    terms: '/term/eula',
    privacy: '/term/privacy_policy'
  },
  onLocaleChange,
  onThemeChange
}: SiteViewProps) {
  const [language, setLanguage] = useState(locale)
  const [appearance, setAppearance] = useState(theme)
  const [menuOpen, setMenuOpen] = useState(false)
  const [filter, setFilter] = useState<Filter>('all')
  useEffect(() => setLanguage(locale), [locale])
  useEffect(() => setAppearance(theme), [theme])
  const changeLanguage = () => {
    const next = language === 'ja' ? 'en' : 'ja'
    setLanguage(next)
    onLocaleChange?.(next)
  }
  const changeTheme = () => {
    const next = appearance === 'light' ? 'dark' : 'light'
    setAppearance(next)
    onThemeChange?.(next)
  }
  const sectionHref = (section: string) => (page === 'content' ? `${links.home}#${section}` : `#${section}`)
  const japanese = language === 'ja'
  const copy = (ja: string, en: string) => (japanese ? ja : en)
  const filters: { key: Filter; label: string }[] = [
    { key: 'all', label: copy('すべて', 'All') },
    { key: 'app', label: copy('アプリ', 'Apps') },
    { key: 'web', label: copy('Webサービス', 'Web services') },
    { key: 'tool', label: copy('開発ツール', 'Developer tools') }
  ]
  const listedProjects =
    page === 'home'
      ? projects.slice(0, 4)
      : projects.filter((project) => filter === 'all' || project.category === filter)
  return (
    <div className={`nkb-site nkb-${appearance}`} lang={language}>
      <div className='nkb-background' aria-hidden='true'>
        <Background />
      </div>
      <header className='nkb-header'>
        <div className='nkb-header-inner'>
          <a href={links.home} className='nkb-brand' aria-label='NEVER KNOWS BEST'>
            <span className='nkb-brand-mark' aria-hidden='true'>
              <i />
              <i />
              <i />
            </span>
            <span>NEVER KNOWS BEST</span>
          </a>
          <nav className='nkb-desktop-nav' aria-label={copy('メインナビゲーション', 'Main navigation')}>
            <a href={links.projects}>{copy('プロダクト', 'Products')}</a>
            <a href={sectionHref('practice')}>{copy('開発について', 'Approach')}</a>
            <a href={sectionHref('about')}>{copy('事業概要', 'About')}</a>
          </nav>
          <div className='nkb-header-actions'>
            <button
              type='button'
              className='nkb-icon-button'
              aria-label={copy('表示テーマを切り替える', 'Switch theme')}
              onClick={changeTheme}
            >
              {appearance === 'light' ? <Moon size={17} /> : <Sun size={17} />}
            </button>
            <button
              className='nkb-locale-button'
              type='button'
              aria-label={copy('表示言語を切り替える', 'Switch language')}
              onClick={changeLanguage}
            >
              {japanese ? 'EN' : 'JA'}
            </button>
            <a href={sectionHref('contact')} className='nkb-header-contact'>
              {copy('お問い合わせ', 'Contact')} <ArrowUpRight size={14} />
            </a>
            <button
              type='button'
              className='nkb-mobile-menu nkb-icon-button'
              aria-expanded={menuOpen}
              aria-controls='nkb-mobile-nav'
              aria-label={copy('メニュー', 'Menu')}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={21} /> : <Menu size={21} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav
            id='nkb-mobile-nav'
            className='nkb-mobile-nav'
            aria-label={copy('モバイルナビゲーション', 'Mobile navigation')}
          >
            {[
              [links.projects, copy('プロダクト', 'Products')],
              [sectionHref('practice'), copy('開発について', 'Approach')],
              [sectionHref('about'), copy('事業概要', 'About')],
              [sectionHref('contact'), copy('お問い合わせ', 'Contact')]
            ].map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)}>
                {label}
                <ArrowUpRight size={16} />
              </a>
            ))}
          </nav>
        )}
      </header>
      <main id='top'>
        {page === 'content' ? (
          <div className='nkb-content nkb-container'>{children}</div>
        ) : (
          <>
            {page === 'home' ? (
              <section className='nkb-hero nkb-container'>
                <p className='nkb-eyebrow'>
                  <span />
                  INDEPENDENT SOFTWARE DEVELOPMENT
                </p>
                <h1>
                  {copy('美しく・強く・速く', 'Beautiful. Powerful. Fast.')}
                  <br />
                  <span>{copy('Webもアプリも自在に', 'Web and apps, effortlessly.')}</span>
                </h1>
                <p className='nkb-hero-description'>
                  {copy('あなたのアイデアを動かす技術。', 'Technology that brings your ideas to life.')}
                  <br />
                  {copy(
                    '使う人の体験から、その先の運用まで。',
                    'From the experience of using it to keeping it running.'
                  )}
                  {!japanese && ' '}
                  <br className='nkb-mobile-break' />
                  {copy('アプリとWebサービスを、丁寧につくります。', 'Thoughtfully built apps and web services.')}
                </p>
                <div className='nkb-hero-actions'>
                  <a className='nkb-button nkb-button-primary' href='#projects'>
                    {copy('プロダクトを見る', 'Explore products')}
                    <ArrowRight size={17} />
                  </a>
                  <a className='nkb-button nkb-button-secondary' href={sectionHref('contact')}>
                    {copy('開発について相談する', 'Discuss a project')}
                    <ArrowUpRight size={17} />
                  </a>
                </div>
                <div className='nkb-hero-foot'>
                  <span>Swift / TypeScript / React / Cloudflare</span>
                  <a href='#projects'>
                    SCROLL TO EXPLORE <ArrowDown size={12} />
                  </a>
                </div>
              </section>
            ) : (
              <section className='nkb-product-hero nkb-container'>
                <p className='nkb-eyebrow'>
                  <span />
                  OUR PRODUCTS
                </p>
                <h1>{copy('アイデアを、かたちに。', 'Ideas, made tangible.')}</h1>
                <p>
                  {copy(
                    '公開中のアプリから、開発中のWebサービスまで。',
                    'From released apps to web services in development.'
                  )}
                  <br />
                  {copy(
                    'NEVER KNOWS BESTが取り組むプロダクトをご紹介します。',
                    'Explore projects built by NEVER KNOWS BEST.'
                  )}
                </p>
              </section>
            )}
            <section id='projects' className='nkb-section nkb-container'>
              <div className='nkb-section-heading'>
                <div>
                  <p className='nkb-eyebrow'>{page === 'home' ? 'SELECTED PRODUCTS' : 'PRODUCT COLLECTION'}</p>
                  <h2>
                    {copy('つくっているもの', 'What we build')}
                    <span className='nkb-count'>{String(listedProjects.length).padStart(2, '0')}</span>
                  </h2>
                </div>
                {page === 'home' && (
                  <a className='nkb-text-link' href={links.projects} target='_top'>
                    {copy('すべてのプロダクト', 'All products')}
                    <ArrowRight size={16} />
                  </a>
                )}
              </div>
              {page === 'projects' && (
                <fieldset className='nkb-filters' aria-label={copy('プロダクトの種類', 'Product category')}>
                  {filters.map(({ key, label }) => (
                    <button key={key} type='button' aria-pressed={filter === key} onClick={() => setFilter(key)}>
                      {label}
                      <span>{key === 'all' ? projects.length : projects.filter((p) => p.category === key).length}</span>
                    </button>
                  ))}
                </fieldset>
              )}
              <div className='nkb-project-grid'>
                {listedProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} locale={language} />
                ))}
              </div>
              <p className='nkb-project-note'>
                {copy(
                  '「開発プロジェクト」は、公開ソースを紹介しているものです。サービスの一般公開状況とは区別しています。',
                  'Development projects link to public source code; this does not indicate general service availability.'
                )}
              </p>
            </section>
            <section id='practice' className='nkb-practice'>
              <div className='nkb-container'>
                <div className='nkb-section-heading'>
                  <div>
                    <p className='nkb-eyebrow'>ENGINEERING & APPROACH</p>
                    <h2>{copy('動くだけで、終わらせない。', 'Built beyond the first release.')}</h2>
                  </div>
                  <p className='nkb-section-intro'>
                    {copy('画面の使いやすさと、見えない部分の確かさ。', 'Usable interfaces. Thoughtful foundations.')}
                    <br />
                    {copy('どちらも大切にしています。', 'Both deserve care.')}
                  </p>
                </div>
                <div className='nkb-capabilities'>
                  <article>
                    <span className='nkb-capability-icon'>
                      <Smartphone size={24} />
                    </span>
                    <p className='nkb-eyebrow'>01 / NATIVE APPS</p>
                    <h3>{copy('使い心地をつくる', 'Design the experience')}</h3>
                    <p>
                      {copy(
                        'Swift・SwiftUIで、iPhone・iPad向けのアプリを開発。再生や通信など、端末の機能を活かした体験をつくります。',
                        'Swift and SwiftUI apps for iPhone and iPad, using native playback and networking capabilities.'
                      )}
                    </p>
                    <div className='nkb-tags'>
                      <span>Swift</span>
                      <span>SwiftUI</span>
                      <span>iOS</span>
                    </div>
                  </article>
                  <article>
                    <span className='nkb-capability-icon'>
                      <Code2 size={24} />
                    </span>
                    <p className='nkb-eyebrow'>02 / WEB & BACKEND</p>
                    <h3>{copy('仕組みから組み立てる', 'Build the foundations')}</h3>
                    <p>
                      {copy(
                        'Reactの画面から、認証・データ管理・APIまで。Cloudflareを活用し、サービス全体を組み立てます。',
                        'From React interfaces to authentication, data and APIs. Building complete services with Cloudflare.'
                      )}
                    </p>
                    <div className='nkb-tags'>
                      <span>TypeScript</span>
                      <span>React</span>
                      <span>Cloudflare</span>
                    </div>
                  </article>
                  <article>
                    <span className='nkb-capability-icon'>
                      <Layers3 size={24} />
                    </span>
                    <p className='nkb-eyebrow'>03 / AI & WORKFLOW</p>
                    <h3>{copy('技術を、役立つかたちに', 'Make technology useful')}</h3>
                    <p>
                      {copy(
                        'LLMを取り入れたサービスや、MCPによる開発環境の連携。目的に合わせて、新しい技術を組み合わせます。',
                        'LLM-powered services and MCP integrations, choosing technologies around the purpose they serve.'
                      )}
                    </p>
                    <div className='nkb-tags'>
                      <span>LLM</span>
                      <span>Python</span>
                      <span>Rust / MCP</span>
                    </div>
                  </article>
                </div>
                <div className='nkb-quality'>
                  <div>
                    <p className='nkb-eyebrow'>THE WORK BEHIND THE WORK</p>
                    <h3>{copy('開発の過程も、確かに。', 'Care in the process, too.')}</h3>
                  </div>
                  <div className='nkb-quality-points'>
                    {[
                      copy('型とテストで、変更を確かめる', 'Verify changes with types and tests'),
                      copy('CIで、ビルドと検査を続ける', 'Keep builds and checks in CI'),
                      copy('共通の部品を、整えて育てる', 'Maintain reusable components')
                    ].map((point) => (
                      <p key={point}>
                        <Check size={16} />
                        {point}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </section>
            <section id='about' className='nkb-section nkb-container nkb-about'>
              <div>
                <p className='nkb-eyebrow'>ABOUT THE BUSINESS</p>
                <h2>NEVER KNOWS BEST</h2>
                <p>
                  {copy(
                    '個人事業として、ソフトウェアを開発しています。',
                    'An independent software development business.'
                  )}
                  <br />
                  {copy('小さな便利さも、新しい体験も。', 'Everyday utility and new experiences.')}
                  <br />
                  {copy('技術で実現できることを、ひとつずつ。', 'Exploring what technology can make possible.')}
                </p>
              </div>
              <dl>
                <div>
                  <dt>{copy('屋号', 'Business name')}</dt>
                  <dd>NEVER KNOWS BEST</dd>
                </div>
                <div>
                  <dt>{copy('事業形態', 'Business type')}</dt>
                  <dd>{copy('個人事業', 'Sole proprietorship')}</dd>
                </div>
                <div>
                  <dt>{copy('事業内容', 'Focus')}</dt>
                  <dd>{copy('アプリ・Webサービスの開発', 'App and web service development')}</dd>
                </div>
                <div>
                  <dt>{copy('開発活動', 'Development')}</dt>
                  <dd>
                    <a href='https://github.com/qtmleap' target='_blank' rel='noreferrer'>
                      GitHub / qtmleap <ArrowUpRight size={14} />
                    </a>
                  </dd>
                </div>
              </dl>
            </section>
            <section id='contact' className='nkb-container nkb-contact'>
              <div>
                <p className='nkb-eyebrow'>LET’S BUILD SOMETHING</p>
                <h2>{copy('次のアイデアを、一緒に。', 'Let’s bring your next idea to life.')}</h2>
                <p>
                  {copy('開発のご相談、プロダクトへのご意見など。', 'Project discussions and product feedback.')}
                  <br />
                  {copy('まずは、やりたいことをお聞かせください。', 'Start with what you would like to build.')}
                </p>
              </div>
              <div className='nkb-contact-actions'>
                <a
                  className='nkb-button nkb-button-primary'
                  href='https://github.com/tkgstrator'
                  target='_blank'
                  rel='noreferrer'
                >
                  {copy('開発者のプロフィールを見る', 'Visit the developer profile')}
                  <ArrowUpRight size={17} />
                </a>
                <a className='nkb-text-link' href={links.support}>
                  {copy('アプリのサポートはこちら', 'App support')}
                  <ArrowRight size={16} />
                </a>
              </div>
            </section>
          </>
        )}
      </main>
      <footer className='nkb-footer nkb-container'>
        <div>
          <strong>NEVER KNOWS BEST</strong>
          <span>© {new Date().getFullYear()} NEVER KNOWS BEST</span>
        </div>
        <nav aria-label={copy('フッターナビゲーション', 'Footer navigation')}>
          <a href='https://github.com/qtmleap' target='_blank' rel='noreferrer'>
            GitHub <ArrowUpRight size={12} />
          </a>
          <a href={links.terms}>{copy('利用規約', 'Terms')}</a>
          <a href={links.privacy}>{copy('プライバシーポリシー', 'Privacy')}</a>
        </nav>
      </footer>
    </div>
  )
}
